/**
 * Folo-aligned Design Tokens
 *
 * 单一真相源（single source of truth）。
 * - palette.ts、theme.ts、motion.ts 都从这里派生
 * - 暗色优先；亮色作为倒置保底
 * - 与 Tailwind zinc 对齐，方便心理映射
 */

// ─────────────────────────────────────────────────────────────────────────────
// Color · Zinc 系基础灰
// ─────────────────────────────────────────────────────────────────────────────

export const zinc = {
  50: '#fafafa',
  100: '#f4f4f5',
  200: '#e4e4e7',
  300: '#d4d4d8',
  400: '#a1a1aa',
  500: '#71717a',
  600: '#52525b',
  700: '#3f3f46',
  800: '#27272a',
  900: '#18181b',
  950: '#09090b',
} as const;

// Vivid Violet — 品牌色，对齐 Folo 紫色调
export const accent = {
  50: '#f5f3ff',
  100: '#ede9fe',
  200: '#ddd6fe',
  300: '#c4b5fd',
  400: '#a78bfa',
  500: '#8b5cf6',
  600: '#7c3aed',
  700: '#6d28d9',
  800: '#5b21b6',
  900: '#4c1d95',
  950: '#2e1065',
} as const;

export const semantic = {
  success: '#10b981',
  successSubtle: 'rgba(16, 185, 129, 0.12)',
  warning: '#f59e0b',
  warningSubtle: 'rgba(245, 158, 11, 0.12)',
  error: '#ef4444',
  errorSubtle: 'rgba(239, 68, 68, 0.12)',
  info: '#3b82f6',
  infoSubtle: 'rgba(59, 130, 246, 0.12)',
} as const;

/** 语义化 surface elevation（暗色优先） */
export const colorTokens = {
  dark: {
    background: {
      base: zinc[950], // body
      surface: zinc[900], // 默认卡片
      raised: zinc[800], // hover/active
      overlay: 'rgba(9, 9, 11, 0.72)', // modal backdrop
    },
    border: {
      subtle: zinc[800],
      default: zinc[700],
      strong: zinc[600],
    },
    text: {
      primary: zinc[50],
      secondary: zinc[400],
      tertiary: zinc[500],
      disabled: zinc[600],
      inverse: zinc[900],
    },
    accent: {
      default: accent[400], // 暗底用稍浅色，避免刺眼
      hover: accent[300],
      pressed: accent[500],
      subtle: 'rgba(139, 92, 246, 0.15)',
      ring: 'rgba(139, 92, 246, 0.45)',
    },
  },
  light: {
    background: {
      base: zinc[50],
      surface: '#ffffff',
      raised: zinc[100],
      overlay: 'rgba(0, 0, 0, 0.40)',
    },
    border: {
      subtle: zinc[200],
      default: zinc[300],
      strong: zinc[400],
    },
    text: {
      primary: zinc[900],
      secondary: zinc[600],
      tertiary: zinc[500],
      disabled: zinc[400],
      inverse: zinc[50],
    },
    accent: {
      default: accent[600],
      hover: accent[700],
      pressed: accent[800],
      subtle: accent[100],
      ring: 'rgba(124, 58, 237, 0.35)',
    },
  },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Typography
// ─────────────────────────────────────────────────────────────────────────────

export const fontFamily = {
  sans: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif',
  display: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  mono: '"JetBrains Mono", "SF Mono", Menlo, Consolas, "Roboto Mono", monospace',
  serif: 'Georgia, "Source Han Serif", "Noto Serif SC", serif',
} as const;

export const typography = {
  display: {
    fontFamily: fontFamily.display,
    fontSize: '2.25rem', // 36
    fontWeight: 700,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
  },
  h2: {
    fontFamily: fontFamily.display,
    fontSize: '1.5rem', // 24
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  h3: {
    fontFamily: fontFamily.display,
    fontSize: '1.25rem', // 20
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  },
  h4: {
    fontFamily: fontFamily.display,
    fontSize: '1.125rem', // 18
    fontWeight: 500,
    lineHeight: 1.4,
    letterSpacing: '0',
  },
  bodyLg: {
    fontFamily: fontFamily.sans,
    fontSize: '1.125rem',
    fontWeight: 400,
    lineHeight: 1.6,
    letterSpacing: '0',
  },
  body: {
    fontFamily: fontFamily.sans,
    fontSize: '1rem', // 16
    fontWeight: 400,
    lineHeight: 1.6,
    letterSpacing: '0',
  },
  bodySm: {
    fontFamily: fontFamily.sans,
    fontSize: '0.875rem', // 14
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0',
  },
  caption: {
    fontFamily: fontFamily.sans,
    fontSize: '0.75rem', // 12
    fontWeight: 500,
    lineHeight: 1.4,
    letterSpacing: '0.01em',
  },
  mono: {
    fontFamily: fontFamily.mono,
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0',
  },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Spacing · 4px scale
// ─────────────────────────────────────────────────────────────────────────────

export const spacing = {
  0: '0px',
  0.5: '2px',
  1: '4px',
  1.5: '6px',
  2: '8px',
  2.5: '10px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  8: '32px',
  10: '40px',
  12: '48px',
  16: '64px',
  20: '80px',
  24: '96px',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Radius
// ─────────────────────────────────────────────────────────────────────────────

export const radius = {
  none: '0',
  xs: '2px',
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  '2xl': '20px',
  full: '9999px',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Shadow（4 档复合投影）
// ─────────────────────────────────────────────────────────────────────────────

export const shadow = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
  // 暗色专属：内描边模拟物理厚度
  insetSubtle: 'inset 0 1px 0 rgba(255, 255, 255, 0.04)',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Motion
// ─────────────────────────────────────────────────────────────────────────────

export const duration = {
  instant: 0,
  fast: 100,
  base: 200,
  slow: 300,
  slower: 500,
} as const;

export const easing = {
  standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
  emphasized: 'cubic-bezier(0.3, 0, 0, 1)',
  decelerate: 'cubic-bezier(0, 0, 0.2, 1)',
  accelerate: 'cubic-bezier(0.4, 0, 1, 1)',
} as const;

export const transition = {
  fast: `all ${duration.fast}ms ${easing.standard}`,
  base: `all ${duration.base}ms ${easing.standard}`,
  slow: `all ${duration.slow}ms ${easing.standard}`,
  colors: `color ${duration.base}ms ${easing.standard}, background-color ${duration.base}ms ${easing.standard}, border-color ${duration.base}ms ${easing.standard}`,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Z-Index
// ─────────────────────────────────────────────────────────────────────────────

export const zIndex = {
  base: 1,
  dropdown: 10,
  sticky: 20,
  drawer: 30,
  modal: 40,
  popover: 50,
  toast: 100,
  commandPalette: 110,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Layout
// ─────────────────────────────────────────────────────────────────────────────

export const layout = {
  sidebarWidth: '240px',
  sidebarCollapsedWidth: '64px',
  articleListWidth: '380px',
  readerMaxWidth: '720px',
  topbarHeight: '56px',
  bottomNavHeight: '56px',
  contentMaxWidth: '1440px',
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Combined export
// ─────────────────────────────────────────────────────────────────────────────

export const tokens = {
  zinc,
  accent,
  semantic,
  color: colorTokens,
  fontFamily,
  typography,
  spacing,
  radius,
  shadow,
  duration,
  easing,
  transition,
  zIndex,
  layout,
} as const;

export type DesignTokens = typeof tokens;
